import { byId } from './modules/dom.js';
import {
  createCategoryPathSegmentFromCategoryTitle,
  buildChannelPathForIndex,
  parseChannelRouteFromPathname
} from './modules/channelRoutes.js';
import { getIrBlasterElements, applyIrPowerState, flashIr } from './modules/irBlaster.js';
import { renderTicker } from './modules/ticker.js';
import { getPlayableVideoEl } from './modules/video.js';
import { fetchCategorizedChannelsWithFallback } from './modules/channelsData.js';
import {
  createMuxStreamSourceFromPublicVideoIdentifier,
  createMuxPosterImageUrlFromPublicVideoIdentifier,
  ensurePlaceholderChannels,
  buildChannelElements,
  setMuteOnChannels,
  switchChannel as switchChannelCore,
  destroyChannels
} from './modules/channels.js';
import { renderMobileRemoteUI } from './modules/peerMobile.js';
import {
  getBrowserFullscreenEnterIconSvg,
  getBrowserFullscreenExitIconSvg,
  isBrowserFullscreen,
  onBrowserFullscreenChange,
  toggleBrowserFullscreen
} from './modules/browserFullscreen.js';
import { updateDocumentSeo, applyHomepageSeo } from './modules/seo.js';
import {
  buildPageTitle,
  CONTACT_EMAILS,
  getChannelCategoryLabelForSeo,
  REPRESENTATION_CONTACTS
} from './modules/seoConfig.js';

export class VideoRemoteApp {
  constructor() {
    // ── Constants ──
    const PLAY_ICON_SVG =
      '<svg class="play-pause-icon" viewBox="0 0 24 24" aria-hidden="true"><polygon points="8,6 19,12 8,18" fill="currentColor"></polygon></svg>';
    const PAUSE_ICON_SVG =
      '<svg class="play-pause-icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="6" width="4" height="12" rx="1" fill="currentColor"></rect><rect x="13" y="6" width="4" height="12" rx="1" fill="currentColor"></rect></svg>';

    // ── State ──
    const state = (this.state = {
      currentChannelIndex: 0,
      isMuted: true,
      isPaused: false,
      isPowered: true,
      isTxtOpen: false,
      isContactOpen: false,
      catalogVideosPlayedCount: 0,
      catalogPlaybackComplete: false
    });

    // { name, src, el (video or canvas), hlsInstance? }
    let channels = (this.channels = []);
    let channelCategories = (this.categories = []);
    let peerInstance = null;
    let remoteConnection = null;
    let resumePlaybackAfterTabVisible = false;

    const dom = (this.dom = {
      tvWrapper: byId('tvWrapper'),
      screenInner: byId('screenInner'),
      staticCanvas: byId('staticCanvas'),
      powerOffMessage: byId('powerOffMessage'),
      channelOsd: byId('channelOsd'),
      muteOsd: byId('muteOsd'),
      contactOsdBtn: byId('contactOsdBtn'),
      contactOverlay: byId('contactOverlay'),
      contactCloseBtn: byId('contactCloseBtn'),
      contactAbout: byId('contactAbout'),
      contactEmailLinks: byId('contactEmailLinks'),
      contactRepresentationLinks: byId('contactRepresentationLinks'),
      peerOsd: byId('peerOsd'),
      remoteDisplay: byId('remoteDisplay'),
      qrcode: byId('qrcode'),
      sessionIdDisplay: byId('sessionIdDisplay'),
      txtOverlay: byId('txtOverlay'),
      txtCloseBtn: byId('txtCloseBtn'),
      txtChannels: byId('txtChannels')
    });

    const irBlasterElements = getIrBlasterElements();
    const app = this;

    const disposers = [];
    const on = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options);
      disposers.push(() => target.removeEventListener(type, handler, options));
    };

    let destroyed = false;
    this.destroy = () => {
      if (destroyed) return;
      destroyed = true;

      // Stop static animation.
      if (staticAnim) cancelAnimationFrame(staticAnim);
      staticAnim = null;

      // Destroy HLS + pause videos.
      destroyChannels(channels);

      // Tear down PeerJS host side.
      try {
        if (remoteConnection && typeof remoteConnection.close === 'function') remoteConnection.close();
      } catch { }
      remoteConnection = null;

      try {
        if (peerInstance && typeof peerInstance.destroy === 'function') peerInstance.destroy();
      } catch { }
      peerInstance = null;

      // Tear down PeerJS mobile side.
      try {
        if (mobileConnection && typeof mobileConnection.close === 'function') mobileConnection.close();
      } catch { }
      mobileConnection = null;

      try {
        if (mobilePeer && typeof mobilePeer.destroy === 'function') mobilePeer.destroy();
      } catch { }
      mobilePeer = null;

      // Remove listeners last.
      disposers.splice(0).forEach(dispose => {
        try {
          dispose();
        } catch { }
      });
    };

    on(window, 'pagehide', this.destroy);
    on(window, 'beforeunload', this.destroy);

    const TXT_ABOUT_COPY =
      'Bedroom is the London duo of Soren Harrison and Amir Hossain. Since they were teenagers their collaboration has spanned graphic design, photography, and moving image, jumping from the fuzzy and homemade to the pop-y and dynamic. Known for vibrant color, inventive art direction, and idiosyncratic compositions, they\'ve created iconic visual identities for Arlo Parks, beabadoobee, The 1975, Måneskin, Benee, and Chloe Qisha. Commercially, their portfolio includes campaigns for McDonalds, Oreo, Converse, Superdry, Dr. Martens, ASOS, and CASS.';

    function getParsedChannelRoute() {
      try {
        const pathname = (window.location && window.location.pathname) || '';
        return parseChannelRouteFromPathname(pathname);
      } catch {
        return null;
      }
    }

    function getRequestedChannelSlug() {
      try {
        const fromGlobal = window && window.__CHANNEL_SLUG__ ? String(window.__CHANNEL_SLUG__) : '';
        if (fromGlobal) return fromGlobal.trim();
      } catch { }

      try {
        const meta = document.querySelector('meta[name="channel-slug"]');
        const metaValue = meta && meta.getAttribute('content') ? String(meta.getAttribute('content')) : '';
        if (metaValue) return metaValue.trim();
      } catch { }

      const parsedRoute = getParsedChannelRoute();
      return parsedRoute ? parsedRoute.slug : '';
    }

    function getRequestedCategoryPathSegment() {
      const parsedRoute = getParsedChannelRoute();
      return parsedRoute ? parsedRoute.categoryPathSegment : '';
    }

    function findChannelIndexByRequestedRoute({ slug, categoryPathSegment } = {}) {
      const trimmedSlug = String(slug || '').trim();
      if (!trimmedSlug) return -1;

      const trimmedCategoryPathSegment = String(categoryPathSegment || '').trim();
      if (trimmedCategoryPathSegment) {
        const categoryMatchIndex = channels.findIndex(channel => {
          if (!channel || channel.slug !== trimmedSlug) return false;
          const channelCategoryPathSegment = createCategoryPathSegmentFromCategoryTitle(channel.categoryTitle);
          return channelCategoryPathSegment === trimmedCategoryPathSegment;
        });
        if (categoryMatchIndex >= 0) return categoryMatchIndex;
      }

      return channels.findIndex(channel => channel && channel.slug === trimmedSlug);
    }

    async function initializeChannelsFromOpenSheet() {
      const requestedSlug = getRequestedChannelSlug();
      let initialIndex = 0;

      try {
        const opensheetUrl =
          (window && window.__OPENSHEET_URL__ ? String(window.__OPENSHEET_URL__) : '').trim() ||
          (import.meta && import.meta.env && import.meta.env.VITE_OPENSHEET_URL
            ? String(import.meta.env.VITE_OPENSHEET_URL)
            : ''
          ).trim() ||
          (import.meta && import.meta.env && import.meta.env.OPENSHEET_URL
            ? String(import.meta.env.OPENSHEET_URL)
            : ''
          ).trim();

        const tabNamesString =
          (window && window.__OPENSHEET_TAB_NAMES__
            ? String(window.__OPENSHEET_TAB_NAMES__)
            : ''
          ).trim() ||
          (import.meta && import.meta.env && import.meta.env.VITE_OPENSHEET_TAB_NAMES
            ? String(import.meta.env.VITE_OPENSHEET_TAB_NAMES)
            : ''
          ).trim() ||
          (import.meta && import.meta.env && import.meta.env.OPENSHEET_TAB_NAMES
            ? String(import.meta.env.OPENSHEET_TAB_NAMES)
            : ''
          ).trim();

        const { categories, channels: normalizedChannels } = await fetchCategorizedChannelsWithFallback({
          opensheetUrl,
          tabNames: tabNamesString
        });

        channelCategories = categories || [];
        app.categories = channelCategories;

        // Build channels directly from rows so each channel keeps its own slug/title/description
        // even if muxIds repeat (Object keying would otherwise overwrite meta).
        channels = (normalizedChannels || []).map((row, channelIndex) => {
          const muxId = row.muxId;
          return {
            name: `CH${channelIndex + 1}`,
            src: createMuxStreamSourceFromPublicVideoIdentifier(muxId),
            muxPublicVideoIdentifier: muxId,
            posterUrl: createMuxPosterImageUrlFromPublicVideoIdentifier(muxId),
            file: `Mux ${muxId}`,
            title: row.title,
            slug: row.slug,
            description: row.description,
            uploadDate: row.uploadDate,
            sheetName: row.sheetName,
            categoryTitle: row.categoryTitle
          };
        });

        if (requestedSlug) {
          const requestedCategoryPathSegment = getRequestedCategoryPathSegment();
          const idx = findChannelIndexByRequestedRoute({
            slug: requestedSlug,
            categoryPathSegment: requestedCategoryPathSegment
          });
          if (idx >= 0) initialIndex = idx;
        }
      } catch (error) {
        // OpenSheet and /channels.json both failed; fall back to placeholder channels.
        console.warn('Channel initialization failed:', error);
      }

      app.channels = channels;
      startTV(initialIndex);
    }

    function startTV(initialChannelIndex = 0) {
      channels = ensurePlaceholderChannels(channels, { placeholderCount: 3 });
      app.channels = channels;
      if (channels.length === 0) return;

      const appLoadingElement = byId('appLoading');
      if (appLoadingElement) appLoadingElement.hidden = true;

      if (dom.tvWrapper) dom.tvWrapper.style.display = 'flex';
      setRemoteIrDotPowerState();

      buildChannels();
      renderContactOverlay();
      updateContactButtonVisibility();
      updatePowerOffMessageVisibility();
      // Ensure the bottom-left mute indicator reflects the initial muted state on first load.
      setMuteState(state.isMuted);
      bindDesktopControlButtons();
      enableDesktopRemoteDragging();
      initStaticCanvas();
      const safeIndex =
        Number.isFinite(initialChannelIndex) && initialChannelIndex >= 0 && initialChannelIndex < channels.length
          ? initialChannelIndex
          : 0;
      switchChannel(safeIndex, false);
      bindHomepagePopstateHandler();
      renderTxtOverlayIfOpen();
      initPeer();
      syncAllFullscreenButtonIcons();
    }

    function isMainSiteViewportWideEnoughForDraggableRemoteSideColumn() {
      if (document.documentElement && document.documentElement.classList.contains('mobile-remote-mode')) {
        return false;
      }
      // Keep in sync with `styles.css` `@media (max-width: 900px)` rules for `.remote-side-column`.
      return window.matchMedia('(min-width: 901px)').matches;
    }

    function clearMainSiteRemoteSideColumnDragInlineStyles(columnElement) {
      if (!columnElement) return;
      columnElement.style.left = '';
      columnElement.style.top = '';
      columnElement.style.right = '';
      columnElement.style.bottom = '';
      columnElement.style.transform = '';
    }

    function enableDesktopRemoteDragging() {
      const columnElement =
        document.querySelector('.remote-side-column') || document.querySelector('.remote');
      if (!columnElement) return;

      // Only makes sense for the on-screen remote (not the phone remote page).
      if (document.documentElement && document.documentElement.classList.contains('mobile-remote-mode')) return;

      if (!isMainSiteViewportWideEnoughForDraggableRemoteSideColumn()) return;

      const STORAGE_KEY = 'desktopRemoteColumnPositionV1';
      const DRAG_START_THRESHOLD_PX = 6;

      const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

      const applyPosition = nextPos => {
        if (!nextPos) return;
        const x = Number(nextPos.x);
        const y = Number(nextPos.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;

        columnElement.style.right = 'auto';
        columnElement.style.bottom = 'auto';
        columnElement.style.transform = 'none';
        columnElement.style.left = `${x}px`;
        columnElement.style.top = `${y}px`;
      };

      const getSavedPosition = () => {
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (!raw) return null;
          const parsed = JSON.parse(raw);
          if (!parsed || typeof parsed !== 'object') return null;
          return { x: parsed.x, y: parsed.y };
        } catch {
          return null;
        }
      };

      const savePosition = pos => {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
        } catch { }
      };

      const clampToViewport = pos => {
        const rect = columnElement.getBoundingClientRect();
        const maxX = Math.max(0, window.innerWidth - rect.width);
        const maxY = Math.max(0, window.innerHeight - rect.height);
        return {
          x: clamp(pos.x, 0, maxX),
          y: clamp(pos.y, 0, maxY)
        };
      };

      const bootstrapPosition = () => {
        const saved = getSavedPosition();
        if (saved) {
          applyPosition(clampToViewport({ x: Number(saved.x), y: Number(saved.y) }));
          return;
        }

        // Convert the CSS default (right/top/transform) into an explicit left/top so dragging is stable.
        const rect = columnElement.getBoundingClientRect();
        applyPosition(clampToViewport({ x: rect.left, y: rect.top }));
      };

      bootstrapPosition();

      let drag = null;
      let suppressClick = false;

      const isInteractiveTarget = target => {
        if (!target || !(target instanceof Element)) return false;
        return Boolean(target.closest('button, a, input, textarea, select, [role="button"]'));
      };

      const onPointerDown = pointerEvent => {
        if (!isMainSiteViewportWideEnoughForDraggableRemoteSideColumn()) return;

        if (!pointerEvent || pointerEvent.button !== undefined) {
          // Only left click for mouse; touch/pen are fine.
          if (pointerEvent.button !== undefined && pointerEvent.button !== 0) return;
        }

        if (isInteractiveTarget(pointerEvent.target)) return;

        const rect = columnElement.getBoundingClientRect();
        const start = {
          pointerId: pointerEvent.pointerId,
          startX: pointerEvent.clientX,
          startY: pointerEvent.clientY,
          originLeft: rect.left,
          originTop: rect.top,
          moved: false
        };
        drag = start;
        suppressClick = false;

        try {
          columnElement.setPointerCapture(pointerEvent.pointerId);
        } catch { }
      };

      const onPointerMove = pointerEvent => {
        if (!isMainSiteViewportWideEnoughForDraggableRemoteSideColumn()) return;
        if (!drag) return;
        if (pointerEvent.pointerId !== drag.pointerId) return;

        const dx = pointerEvent.clientX - drag.startX;
        const dy = pointerEvent.clientY - drag.startY;
        const dist = Math.hypot(dx, dy);

        if (!drag.moved && dist >= DRAG_START_THRESHOLD_PX) {
          drag.moved = true;
          suppressClick = true;
          columnElement.classList.add('is-dragging');
        }

        if (!drag.moved) return;

        const next = clampToViewport({
          x: drag.originLeft + dx,
          y: drag.originTop + dy
        });
        applyPosition(next);
        savePosition(next);

        if (pointerEvent.cancelable) pointerEvent.preventDefault();
      };

      const endDrag = pointerEvent => {
        if (!drag) return;
        if (pointerEvent && pointerEvent.pointerId !== drag.pointerId) return;

        columnElement.classList.remove('is-dragging');
        drag = null;
        // Let one frame pass so "click" (if any) can be suppressed consistently.
        setTimeout(() => {
          suppressClick = false;
        }, 0);
      };

      const onClickCapture = clickEvent => {
        if (!suppressClick) return;
        // If we dragged, don't trigger accidental clicks on the remote container.
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      };

      columnElement.classList.add('is-draggable');
      on(columnElement, 'pointerdown', onPointerDown);
      on(window, 'pointermove', onPointerMove, { passive: false });
      on(window, 'pointerup', endDrag);
      on(window, 'pointercancel', endDrag);
      on(columnElement, 'click', onClickCapture, true);

      on(window, 'resize', () => {
        if (!isMainSiteViewportWideEnoughForDraggableRemoteSideColumn()) {
          clearMainSiteRemoteSideColumnDragInlineStyles(columnElement);
          columnElement.classList.remove('is-draggable', 'is-dragging');
          drag = null;
          return;
        }

        columnElement.classList.add('is-draggable');

        // Keep the remote column on-screen after resizes.
        const rect = columnElement.getBoundingClientRect();
        const clamped = clampToViewport({ x: rect.left, y: rect.top });
        applyPosition(clamped);
        savePosition(clamped);
      });
    }

    function bindDesktopControlButtons() {
      const powerButton = byId('powerBtn');
      on(powerButton, 'click', togglePower);

      const playPauseButton = byId('playPauseBtn');
      on(playPauseButton, 'click', togglePlayPause);

      const muteButton = byId('muteBtn');
      on(muteButton, 'click', toggleMute);

      const channelUpButton = byId('channelUpBtn');
      on(channelUpButton, 'click', channelUp);

      const channelDownButton = byId('channelDownBtn');
      on(channelDownButton, 'click', channelDown);

      const txtButton = byId('txtBtn');
      on(txtButton, 'click', () => toggleTxtOverlay());

      const fullscreenButton = byId('fullscreenBtn');
      if (fullscreenButton) {
        on(fullscreenButton, 'click', () => {
          hideDesktopQrPanelAfterRemoteInteraction();
          void toggleBrowserFullscreen();
        });
      }

      on(dom.txtCloseBtn, 'click', () => setTxtOpen(false));
      on(dom.txtOverlay, 'click', overlayEvent => {
        if (overlayEvent && overlayEvent.target === dom.txtOverlay) setTxtOpen(false);
      });

      on(dom.muteOsd, 'click', toggleMute);

      on(dom.contactOsdBtn, 'click', () => toggleContactOverlay());
      on(dom.contactCloseBtn, 'click', () => setContactOpen(false));
      on(dom.contactOverlay, 'click', overlayEvent => {
        if (overlayEvent && overlayEvent.target === dom.contactOverlay) setContactOpen(false);
      });
    }

    // ── Build video elements ──
    function finishCatalogPlayback() {
      state.catalogPlaybackComplete = true;
      switchChannel(0, true, true, false, false);
    }

    function handleVideoEnded(endedEvent) {
      if (!state.isPowered || state.isPaused) return;
      if (state.catalogPlaybackComplete) return;
      const activeChannel = channels[state.currentChannelIndex];
      const endedVideoElement = endedEvent && endedEvent.target;
      if (!activeChannel?.el || activeChannel.el !== endedVideoElement) return;

      state.catalogVideosPlayedCount += 1;
      if (state.catalogVideosPlayedCount >= channels.length) {
        finishCatalogPlayback();
        return;
      }

      const nextIndex = (state.currentChannelIndex + 1) % channels.length;
      switchChannel(nextIndex, true, true, false);
    }

    function buildChannels() {
      const screenElement = dom.screenInner || document.getElementById('screenInner');
      buildChannelElements({
        channels,
        screenElement,
        isMuted: state.isMuted,
        onVideoEnded: handleVideoEnded
      });
    }

    function updateContactButtonVisibility() {
      const contactButtonElement = dom.contactOsdBtn;
      if (!contactButtonElement) return;
      contactButtonElement.hidden = !state.isPowered;
    }

    function updatePowerOffMessageVisibility() {
      const powerOffMessageElement = dom.powerOffMessage;
      if (!powerOffMessageElement) return;
      const showMessage = !state.isPowered;
      powerOffMessageElement.hidden = !showMessage;
      powerOffMessageElement.setAttribute('aria-hidden', showMessage ? 'false' : 'true');
    }

    // ── Static canvas ──
    let staticAnim = null;
    function resizeStaticCanvasToScreen() {
      const canvas = dom.staticCanvas || document.getElementById('staticCanvas');
      const screenInner = dom.screenInner || document.getElementById('screenInner');
      if (!canvas || !screenInner) return;
      const width = Math.max(1, Math.floor(screenInner.clientWidth));
      const height = Math.max(1, Math.floor(screenInner.clientHeight));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
    }

    function initStaticCanvas() {
      resizeStaticCanvasToScreen();
      on(window, 'resize', resizeStaticCanvasToScreen);
    }

    function showStatic(duration, onCompleteCallback) {
      const canvas = dom.staticCanvas || document.getElementById('staticCanvas');
      const canvasContext = canvas.getContext('2d');
      resizeStaticCanvasToScreen();
      canvas.classList.add('visible');
      const startTimestamp = performance.now();
      function draw(animationTimestamp) {
        const progress = (animationTimestamp - startTimestamp) / duration;
        if (progress >= 1) {
          canvas.classList.remove('visible');
          if (onCompleteCallback) onCompleteCallback();
          return;
        }
        const imageData = canvasContext.createImageData(canvas.width, canvas.height);
        const pixelData = imageData.data;
        for (let pixelOffset = 0; pixelOffset < pixelData.length; pixelOffset += 4) {
          const grayscaleValue = Math.random() > 0.5 ? Math.floor(Math.random() * 255) : 0;
          pixelData[pixelOffset] = pixelData[pixelOffset + 1] = pixelData[pixelOffset + 2] = grayscaleValue;
          pixelData[pixelOffset + 3] = 200 * (1 - progress * 0.7);
        }
        canvasContext.putImageData(imageData, 0, 0);
        staticAnim = requestAnimationFrame(draw);
      }
      if (staticAnim) cancelAnimationFrame(staticAnim);
      staticAnim = requestAnimationFrame(draw);
    }

    // ── Channel switching ──
    function ensureMetaTag(name) {
      try {
        let el = document.querySelector(`meta[name="${CSS.escape(name)}"]`);
        if (el) return el;
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
        return el;
      } catch {
        return null;
      }
    }

    function isRootHomepagePath(pathname) {
      const normalized = String(pathname || '/').replace(/\/+$/, '') || '/';
      return normalized === '/';
    }

    function clearChannelSlugHints() {
      try {
        const slugMeta = ensureMetaTag('channel-slug');
        if (slugMeta) slugMeta.setAttribute('content', '');
        window.__CHANNEL_SLUG__ = '';
      } catch { }
    }

    function applyHomepageSeoAndClearChannelHints() {
      applyHomepageSeo();
      clearChannelSlugHints();
    }

    function updateHeadForChannelIndex(channelIndex) {
      try {
        const idx = Number(channelIndex);
        if (!Number.isFinite(idx) || idx < 0 || idx >= channels.length) return;

        const channel = channels[idx] || {};
        const channelTitle = channel.title || channel.slug || `Channel ${idx + 1}`;
        const categoryLabel = getChannelCategoryLabelForSeo(channel);
        const nextPath = buildChannelPathForIndex(channels, idx);

        updateDocumentSeo({
          channel,
          path: nextPath,
          title: buildPageTitle(categoryLabel ? `${categoryLabel} — ${channelTitle}` : channelTitle)
        });

        // Keep these in sync with the prerender hints so deep links hydrate correctly.
        const slugMeta = ensureMetaTag('channel-slug');
        if (slugMeta) slugMeta.setAttribute('content', channel.slug || '');
        try {
          window.__CHANNEL_SLUG__ = channel.slug || '';
        } catch { }
      } catch { }
    }

    function syncRouteAndSeoAfterChannelSwitch(channelIndex, { userInitiated = false } = {}) {
      try {
        const pathname = (window.location && window.location.pathname) || '/';

        if (isRootHomepagePath(pathname) && !userInitiated) {
          applyHomepageSeoAndClearChannelHints();
          return;
        }

        updateHeadForChannelIndex(channelIndex);

        const nextPath = buildChannelPathForIndex(channels, channelIndex);
        if (!nextPath || pathname === nextPath) return;

        const historyMethod = userInitiated ? 'pushState' : 'replaceState';
        window.history[historyMethod]({}, '', nextPath);
      } catch { }
    }

    function bindHomepagePopstateHandler() {
      on(window, 'popstate', () => {
        try {
          const pathname = (window.location && window.location.pathname) || '/';
          if (isRootHomepagePath(pathname)) {
            applyHomepageSeoAndClearChannelHints();
            switchChannel(0, false, false, false);
            return;
          }

          const slug = getRequestedChannelSlug();
          if (!slug) return;
          const categoryPathSegment = getRequestedCategoryPathSegment();
          const channelIndex = findChannelIndexByRequestedRoute({
            slug,
            categoryPathSegment
          });
          if (channelIndex >= 0) switchChannel(channelIndex, false, false, false);
        } catch { }
      });
    }

    function switchChannel(
      targetChannelIndex,
      animate = true,
      restartPlaybackFromBeginning = false,
      userInitiated = false,
      startPlayback = true
    ) {
      switchChannelCore({
        channels,
        state,
        targetChannelIndex,
        animate,
        restartPlaybackFromBeginning,
        startPlayback,
        showStatic,
        updateOsd,
        updateDots,
        updateRemoteDisplay,
        updatePlayPauseBtn,
        onSwitched: channelIndex => {
          syncRouteAndSeoAfterChannelSwitch(channelIndex, { userInitiated });
          sendRemotePeerStateSync();
        }
      });
    }

    function channelUp() {
      hideDesktopQrPanelAfterRemoteInteraction();
      flashRemoteIrDot();
      switchChannel((state.currentChannelIndex + 1) % channels.length, true, false, true);
    }

    function channelDown() {
      hideDesktopQrPanelAfterRemoteInteraction();
      flashRemoteIrDot();
      switchChannel((state.currentChannelIndex - 1 + channels.length) % channels.length, true, false, true);
    }

    // ── Controls ──
    function getCurrentPlayableVideoElement() {
      return getPlayableVideoEl({
        channels,
        currentChannelIndex: state.currentChannelIndex
      });
    }

    function bindPauseOnHiddenTab() {
      on(document, 'visibilitychange', () => {
        if (!state.isPowered) return;

        const currentChannelVideoElement = getCurrentPlayableVideoElement();
        if (!currentChannelVideoElement || currentChannelVideoElement.tagName !== 'VIDEO') return;

        if (document.hidden) {
          if (!state.isPaused && !state.catalogPlaybackComplete && !currentChannelVideoElement.paused) {
            currentChannelVideoElement.pause();
            resumePlaybackAfterTabVisible = true;
          } else {
            resumePlaybackAfterTabVisible = false;
          }
          return;
        }

        if (!resumePlaybackAfterTabVisible) return;
        resumePlaybackAfterTabVisible = false;

        if (state.isPaused || state.catalogPlaybackComplete) return;

        currentChannelVideoElement.play().catch(() => {});
      });
    }

    function togglePlayPause() {
      flashRemoteIrDot();
      if (!state.isPowered) return;
      hideDesktopQrPanelAfterRemoteInteraction();
      const currentChannelVideoElement = getCurrentPlayableVideoElement();
      if (!currentChannelVideoElement) return;
      if (state.isPaused) {
        currentChannelVideoElement.play().catch(() => {
          // Remote-triggered playback can be blocked by autoplay rules; muted retry is more reliable.
          currentChannelVideoElement.muted = true;
          state.isMuted = true;
          updateSoundButtons();
          currentChannelVideoElement.play().catch(() => { });
        });
        state.isPaused = false;
      } else {
        currentChannelVideoElement.pause();
        state.isPaused = true;
      }
      updatePlayPauseBtn();
      renderTxtOverlayIfOpen();
    }

    function toggleMute() {
      flashRemoteIrDot();
      if (!state.isPowered) return;
      hideDesktopQrPanelAfterRemoteInteraction();
      const nextMutedState = !state.isMuted;
      setMuteState(nextMutedState);
      if (!nextMutedState) {
        attemptAudioUnlockFromButtonAction();
      }
    }

    function setMuteState(nextMutedState) {
      state.isMuted = nextMutedState;
      setMuteOnChannels(channels, nextMutedState);
      if (dom.muteOsd) dom.muteOsd.classList.toggle('visible', nextMutedState);
      updateSoundButtons();
    }

    function attemptAudioUnlockFromButtonAction() {
      const currentChannelVideoElement = getCurrentPlayableVideoElement();
      if (!currentChannelVideoElement) return;
      const playPromise = currentChannelVideoElement.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { });
      }
    }

    function buildSoundIconMarkup(isMutedState) {
      if (isMutedState) {
        return '<svg class="sound-icon" viewBox="0 0 24 24" aria-hidden="true"><polygon points="3,9 8,9 13,5 13,19 8,15 3,15" fill="currentColor"></polygon><line x1="16" y1="8" x2="21" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"></line><line x1="21" y1="8" x2="16" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"></line></svg>';
      }
      return '<svg class="sound-icon" viewBox="0 0 24 24" aria-hidden="true"><polygon points="3,9 8,9 13,5 13,19 8,15 3,15" fill="currentColor"></polygon><path d="M16 9 C18 10.5, 18 13.5, 16 15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path><path d="M18.5 6.5 C22 9.5, 22 14.5, 18.5 17.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg>';
    }

    function updateSoundButtons() {
      const muteButton = document.querySelector('.rbtn-mute');
      if (muteButton) {
        muteButton.innerHTML = buildSoundIconMarkup(state.isMuted);
      }
    }

    function setRemoteIrDotPowerState() {
      applyIrPowerState(irBlasterElements, state.isPowered);
    }

    function flashRemoteIrDot() {
      if (!state.isPowered) {
        setRemoteIrDotPowerState();
        return;
      }
      flashIr(irBlasterElements);
    }

    function togglePower() {
      hideDesktopQrPanelAfterRemoteInteraction();
      flashRemoteIrDot();
      state.isPowered = !state.isPowered;

      const screen = dom.screenInner;
      const muteOnScreenDisplayElement = dom.muteOsd;
      const currentChannel = channels[state.currentChannelIndex];

      if (!state.isPowered) {
        if (currentChannel && currentChannel.el && currentChannel.el.tagName === 'VIDEO') currentChannel.el.pause();

        const remoteDisplayElement = dom.remoteDisplay;
        if (remoteDisplayElement) remoteDisplayElement.textContent = '';
        if (muteOnScreenDisplayElement) muteOnScreenDisplayElement.classList.remove('visible');
        updateContactButtonVisibility();
        updatePowerOffMessageVisibility();

        setRemoteIrDotPowerState();
        showStatic(200, () => {
          if (currentChannel && currentChannel.el) currentChannel.el.classList.remove('active');
          screen.style.background = '#000';
        });
      } else {
        state.catalogVideosPlayedCount = 0;
        state.catalogPlaybackComplete = false;
        updatePowerOffMessageVisibility();
        screen.style.background = '';

        if (currentChannel && currentChannel.el) {
          currentChannel.el.classList.add('active');
          if (currentChannel.el.tagName === 'VIDEO') currentChannel.el.play().catch(() => { });
        }

        state.isPaused = false;
        updateRemoteDisplay();
        if (muteOnScreenDisplayElement) muteOnScreenDisplayElement.classList.toggle('visible', state.isMuted);
        updateContactButtonVisibility();

        setRemoteIrDotPowerState();
        updatePlayPauseBtn();
      }

      renderTxtOverlayIfOpen();
    }

    function hideDesktopQrPanelAfterRemoteInteraction() {
      const qrPanelElement = document.querySelector('.qr-panel');
      if (!qrPanelElement) return;
      qrPanelElement.style.visibility = 'hidden';
      qrPanelElement.style.pointerEvents = 'none';
      qrPanelElement.setAttribute('aria-hidden', 'true');
    }

    // ── OSD updates ──
    function getChannelTitle(channel) {
      if (!channel) return '';
      if (channel.title) return String(channel.title);
      if (channel.file) return String(channel.file);
      if (channel.muxPublicVideoIdentifier) return String(channel.muxPublicVideoIdentifier);
      return '';
    }

    function getChannelDescription(channel) {
      if (!channel || channel.description === undefined || channel.description === null) return '';
      return String(channel.description).trim();
    }

    function getChannelName(channel) {
      if (!channel) return '';
      const fromSheetName =
        channel.sheetName !== undefined && channel.sheetName !== null
          ? String(channel.sheetName).trim()
          : '';
      if (fromSheetName) return fromSheetName;
      return getChannelDescription(channel);
    }

    function getTxtChannelDisplayText(channel) {
      const name = getChannelName(channel);
      const title = getChannelTitle(channel);
      return { name, title };
    }

    function buildChannelVideoLabelLines(channel, channelIndex, { padChannelNumber = false } = {}) {
      const categoryLabel = toUppercaseDisplayText(getChannelCategoryLabelForSeo(channel));
      const title = toUppercaseDisplayText(getChannelTitle(channel));
      const name = toUppercaseDisplayText(getChannelName(channel));
      const channelNumber = padChannelNumber
        ? String(channelIndex + 1).padStart(2, '0')
        : String(channelIndex + 1);
      const lines = [];
      if (categoryLabel) lines.push(categoryLabel);
      lines.push(`CH ${channelNumber}`);
      if (title) lines.push(title);
      if (name) lines.push(name);
      return lines;
    }

    function buildChannelVideoLabelText(channel, channelIndex, options) {
      return buildChannelVideoLabelLines(channel, channelIndex, options).join('\n');
    }

    function toUppercaseDisplayText(value) {
      const text = String(value || '').trim();
      return text ? text.toUpperCase() : '';
    }

    function updateOsd() {
      const channelOnScreenDisplayElement = dom.channelOsd;
      if (!channelOnScreenDisplayElement) return;

      const activeChannel = channels[state.currentChannelIndex];
      channelOnScreenDisplayElement.textContent = buildChannelVideoLabelText(
        activeChannel,
        state.currentChannelIndex
      );

      channelOnScreenDisplayElement.classList.add('visible');
      clearTimeout(channelOnScreenDisplayElement._timer);
      const hasNameLine = Boolean(getChannelName(activeChannel));
      const hideAfterMilliseconds = hasNameLine ? 5500 : 2000;
      channelOnScreenDisplayElement._timer = setTimeout(
        () => channelOnScreenDisplayElement.classList.remove('visible'),
        hideAfterMilliseconds
      );
    }

    function updateDots() {
      document.querySelectorAll('.ch-dot').forEach((channelDotElement, channelIndex) => {
        channelDotElement.classList.toggle('active', channelIndex === state.currentChannelIndex);
      });
    }

    function updateRemoteDisplay() {
      const remoteDisplayElement = dom.remoteDisplay;
      if (!remoteDisplayElement) return;

      const activeChannel = channels[state.currentChannelIndex];
      renderTicker(
        remoteDisplayElement,
        buildChannelVideoLabelText(activeChannel, state.currentChannelIndex, {
          padChannelNumber: true
        })
      );
      renderTxtOverlayIfOpen();
    }

    function toggleTxtOverlay() {
      setTxtOpen(!state.isTxtOpen);
    }

    function setTxtOpen(nextOpen) {
      const next = Boolean(nextOpen);
      if (next) setContactOpen(false);
      state.isTxtOpen = next;
      if (dom.txtOverlay) dom.txtOverlay.hidden = !next;
      if (next) renderTxtOverlay();
    }

    function toggleContactOverlay() {
      setContactOpen(!state.isContactOpen);
    }

    function setContactOpen(nextOpen) {
      const next = Boolean(nextOpen);
      if (next) setTxtOpen(false);
      state.isContactOpen = next;
      if (dom.contactOverlay) dom.contactOverlay.hidden = !next;
      if (next) renderContactOverlay();
    }

    function renderContactOverlay() {
      if (!dom.contactAbout) return;

      dom.contactAbout.textContent = TXT_ABOUT_COPY;

      const contactEmailLinksElement = dom.contactEmailLinks;
      if (contactEmailLinksElement) {
        contactEmailLinksElement.replaceChildren();
        CONTACT_EMAILS.forEach(emailAddress => {
          const emailLinkElement = document.createElement('a');
          emailLinkElement.className = 'txt-overlay-link';
          emailLinkElement.href = `mailto:${emailAddress}`;
          emailLinkElement.textContent = emailAddress;
          contactEmailLinksElement.appendChild(emailLinkElement);
        });
      }

      const contactRepresentationLinksElement = dom.contactRepresentationLinks;
      if (contactRepresentationLinksElement) {
        contactRepresentationLinksElement.replaceChildren();
        REPRESENTATION_CONTACTS.forEach(representationContact => {
          const representationRowElement = document.createElement('div');
          representationRowElement.className = 'txt-overlay-representation-row';
          representationRowElement.append(
            document.createTextNode(`${representationContact.region}: ${representationContact.agency} `)
          );
          const emailLinkElement = document.createElement('a');
          emailLinkElement.className = 'txt-overlay-link';
          emailLinkElement.href = `mailto:${representationContact.email}`;
          emailLinkElement.textContent = representationContact.email;
          representationRowElement.appendChild(emailLinkElement);
          contactRepresentationLinksElement.appendChild(representationRowElement);
        });
      }
    }

    function renderTxtOverlayIfOpen() {
      if (!state.isTxtOpen) return;
      renderTxtOverlay();
    }

    function appendTxtChannelRow(parentElement, channel, idx) {
      const row = document.createElement('div');
      row.className = 'txt-ch-row';
      row.role = 'button';
      row.tabIndex = 0;
      row.setAttribute('aria-label', `Play channel ${idx + 1}`);
      row.addEventListener('click', () => {
        setTxtOpen(false);
        switchChannel(idx, true, true, true);
      });
      row.addEventListener('keydown', keyboardEvent => {
        if (!keyboardEvent) return;
        if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return;
        keyboardEvent.preventDefault();
        setTxtOpen(false);
        switchChannel(idx, true, true, true);
      });

      const left = document.createElement('div');
      left.className = 'txt-ch-left';
      left.textContent = `CH ${String(idx + 1).padStart(2, '0')}`;

      const titleCell = document.createElement('div');
      titleCell.className = 'txt-ch-title';

      const { name, title: videoTitle } = getTxtChannelDisplayText(channel);
      const displayName = toUppercaseDisplayText(name);
      const displayTitle = toUppercaseDisplayText(videoTitle);

      if (displayTitle && displayName) {
        const titleLine = document.createElement('div');
        titleLine.className = 'txt-ch-video-title';
        titleLine.textContent = displayTitle;

        const nameLine = document.createElement('div');
        nameLine.className = 'txt-ch-name';
        nameLine.textContent = displayName;

        titleCell.appendChild(titleLine);
        titleCell.appendChild(nameLine);
      } else {
        titleCell.textContent =
          displayTitle || displayName || (channel && channel.src ? '' : 'NO SIGNAL');
      }

      const right = document.createElement('div');
      right.className = 'txt-ch-right';

      const isActive = idx === state.currentChannelIndex;
      const hasSignal = Boolean(channel && channel.src);

      let status = '-';
      if (!state.isPowered) status = 'OFF';
      else if (!hasSignal) status = 'NO SIGNAL';
      else if (isActive && state.isPaused) status = 'PAUSED';
      else if (isActive && !state.isPaused) status = 'PLAYING';

      right.textContent = status;
      row.classList.toggle('is-active', isActive);

      row.appendChild(left);
      row.appendChild(titleCell);
      row.appendChild(right);
      parentElement.appendChild(row);
    }

    function buildTxtChannelLookupKey(channel, categoryTitleFallback) {
      const categoryTitle = String(
        (channel && channel.categoryTitle) || categoryTitleFallback || ''
      ).trim();
      const slug = String((channel && channel.slug) || '').trim();
      if (!slug) return '';
      const categoryKey = categoryTitle || '__default__';
      return `${categoryKey}\0${slug}`;
    }

    function renderTxtOverlay() {
      if (!dom.txtOverlay || !dom.txtChannels) return;

      dom.txtChannels.textContent = '';

      const channelKeyToGlobalIndex = new Map();
      channels.forEach((channel, idx) => {
        const key = buildTxtChannelLookupKey(channel);
        if (key) channelKeyToGlobalIndex.set(key, idx);
      });

      const categoriesToRender =
        channelCategories && channelCategories.length
          ? channelCategories
          : [
              {
                title: 'Channels',
                channels: channels.map(channel => ({
                  slug: channel.slug,
                  categoryTitle: channel.categoryTitle
                }))
              }
            ];

      categoriesToRender.forEach(category => {
        if (!category || !category.channels || !category.channels.length) return;

        const header = document.createElement('div');
        header.className = 'txt-ch-category';
        const categoryTitle = toUppercaseDisplayText(category.title || 'Channels');
        header.textContent = categoryTitle ? `${categoryTitle}:` : '';
        dom.txtChannels.appendChild(header);

        const categoryTitleForLookup = String(category.title || 'Channels').trim();
        category.channels.forEach(categoryChannel => {
          const key = buildTxtChannelLookupKey(categoryChannel, categoryTitleForLookup);
          const idx = channelKeyToGlobalIndex.get(key);
          if (idx === undefined) return;
          appendTxtChannelRow(dom.txtChannels, channels[idx], idx);
        });
      });
    }

    function updatePlayPauseBtn() {
      const mainPlayPauseButtonElement = document.getElementById('playPauseBtn');
      if (mainPlayPauseButtonElement) {
        mainPlayPauseButtonElement.innerHTML = state.isPaused ? PLAY_ICON_SVG : PAUSE_ICON_SVG;
      }
    }

    // ── PeerJS ──
    async function initPeer() {
      const sessionId = 'video-room-' + Math.random().toString(36).slice(2, 10);
      const sessionIdDisplayElement = document.getElementById('sessionIdDisplay');
      if (sessionIdDisplayElement) sessionIdDisplayElement.textContent = sessionId;
      setPhoneConnectedState(false);

      try {
        const { default: Peer } = await import('peerjs');
        peerInstance = new Peer(sessionId);

        peerInstance.on('open', peerIdentifier => {
          const mobileUrl = `${location.protocol}//${location.host}${location.pathname}?remote=${encodeURIComponent(
            peerIdentifier
          )}`;
          generateQR(mobileUrl);

        });

        peerInstance.on('connection', incomingConnection => {
          remoteConnection = incomingConnection;
          setPhoneConnectedState(true);

          remoteConnection.on('open', () => {
            setPhoneConnectedState(true);

            const peerOnScreenDisplayElement = document.getElementById('peerOsd');
            if (peerOnScreenDisplayElement) {
              peerOnScreenDisplayElement.classList.add('visible');
              setTimeout(() => peerOnScreenDisplayElement.classList.remove('visible'), 3000);
            }

            remoteConnection.send({
              type: 'info',
              channels: channels.length,
              current: state.currentChannelIndex,
              muted: state.isMuted,
              paused: state.isPaused,
              powered: state.isPowered,
              title: getChannelTitle(channels[state.currentChannelIndex]),
              description: getChannelDescription(channels[state.currentChannelIndex]),
              categoryLabel: getChannelCategoryLabelForSeo(channels[state.currentChannelIndex]),
              channelTitles: channels.map(channel => getChannelTitle(channel)),
              channelDescriptions: channels.map(channel => getChannelDescription(channel)),
              channelCategoryLabels: channels.map(channel => getChannelCategoryLabelForSeo(channel))
            });
          });

          remoteConnection.on('data', data => {
            setPhoneConnectedState(true);
            handleRemoteData(data);
          });

          remoteConnection.on('close', () => {
            setPhoneConnectedState(false);
            remoteConnection = null;
          });
        });

        peerInstance.on('error', peerError => {
          console.warn('PeerJS error:', peerError);
          setPhoneConnectedState(false);
        });
      } catch (peerInitializationError) {
        console.warn('PeerJS unavailable:', peerInitializationError);
        setPhoneConnectedState(false);
      }
    }

    function setPhoneConnectedState(isConnected) {
      document.body.classList.toggle('phone-connected', isConnected);
    }

    function sendRemotePeerStateSync() {
      if (!remoteConnection) return;
      remoteConnection.send({
        type: 'state',
        current: state.currentChannelIndex,
        muted: state.isMuted,
        paused: state.isPaused,
        powered: state.isPowered,
        title: getChannelTitle(channels[state.currentChannelIndex]),
        description: getChannelDescription(channels[state.currentChannelIndex]),
        categoryLabel: getChannelCategoryLabelForSeo(channels[state.currentChannelIndex])
      });
    }

    function handleRemoteData(data) {
      if (!data || !data.action) return;

      switch (data.action) {
        case 'ch_up':
          channelUp();
          break;
        case 'ch_down':
          channelDown();
          break;
        case 'play_pause':
          togglePlayPause();
          break;
        case 'mute':
          toggleMute();
          break;
        case 'power':
          togglePower();
          break;
        case 'txt':
          toggleTxtOverlay();
          break;
        case 'contact':
          toggleContactOverlay();
          break;
        case 'fullscreen':
          void toggleBrowserFullscreen();
          break;
        case 'ch_direct':
          if (data.ch !== undefined) switchChannel(data.ch, true, false, true);
          break;
      }

      // Channel changes with static animation defer `state.currentChannelIndex` until `onSwitched`
      // runs; syncing here would send the previous channel and desync the phone ticker.
      const peerStateAlreadySentByChannelSwitch =
        data.action === 'ch_up' || data.action === 'ch_down' || data.action === 'ch_direct';
      if (!peerStateAlreadySentByChannelSwitch) {
        sendRemotePeerStateSync();
      }
    }

    async function generateQR(url) {
      const { default: QRCode } = await import('qrcode');
      const container = dom.qrcode || document.getElementById('qrcode');
      if (!container) return;
      container.innerHTML = '';
      try {
        const canvas = document.createElement('canvas');
        const qrPixelSize = 88;
        canvas.width = qrPixelSize;
        canvas.height = qrPixelSize;
        container.appendChild(canvas);

        await QRCode.toCanvas(canvas, url, {
          width: qrPixelSize,
          margin: 1,
          color: {
            dark: '#1a1208',
            light: '#f5f0e8'
          },
          errorCorrectionLevel: 'M'
        });
      } catch (qrCodeError) {
        container.innerHTML = `<div style="width:88px;height:88px;background:#f5f0e8;display:flex;align-items:center;justify-content:center;font-size:8px;color:#333;text-align:center;padding:6px;">QR unavailable<br>Use session ID</div>`;
      }
    }

    // ── Mobile remote mode ──
    function checkMobileRemote() {
      const params = new URLSearchParams(location.search);
      const remoteId = params.get('remote');
      if (!remoteId) return false;

      mobileUi = renderMobileRemoteUI({
        playIconSvg: PLAY_ICON_SVG,
        pauseIconSvg: PAUSE_ICON_SVG,
        fullscreenEnterIconSvg: getBrowserFullscreenEnterIconSvg(),
        initialMuted: true,
        buildSoundIconMarkup
      });
      initMobilePeer(remoteId, mobileUi);
      return true;
    }

    let mobilePeer = null;
    let mobileConnection = null;
    let mobileCurrentChannel = 0;
    let mobileChannelTitles = [];
    let mobileChannelDescriptions = [];
    let mobileChannelCategoryLabels = [];
    let mobileCurrentTitle = '';
    let mobileCurrentDescription = '';
    let mobileCurrentCategoryLabel = '';
    let mobileUi = null;

    function syncAllFullscreenButtonIcons() {
      const iconHtml = isBrowserFullscreen()
        ? getBrowserFullscreenExitIconSvg()
        : getBrowserFullscreenEnterIconSvg();
      const desktopFullscreenButton = byId('fullscreenBtn');
      if (desktopFullscreenButton) desktopFullscreenButton.innerHTML = iconHtml;
    }

    disposers.push(onBrowserFullscreenChange(syncAllFullscreenButtonIcons));

    async function initMobilePeer(hostId, ui) {
      const { elements } = ui;
      function updateMobileStatus(text) {
        if (elements.display) elements.display.textContent = text;
      }

      function sendMobileAction(action, extra) {
        if (!mobileConnection || !mobileConnection.open) return;
        const data = { action };
        if (extra) Object.assign(data, extra);
        mobileConnection.send(data);
        if (navigator.vibrate) navigator.vibrate(30);
      }

      function bindMobileControlButtons() {
        if (elements.powerButton) {
          on(elements.powerButton, 'click', () => {
            flashRemoteIrDot();
            sendMobileAction('power');
          });
        }

        if (elements.playButton) {
          on(elements.playButton, 'click', () => {
            flashRemoteIrDot();
            sendMobileAction('play_pause');
          });
        }

        if (elements.channelUpButton) {
          on(elements.channelUpButton, 'click', () => {
            flashRemoteIrDot();
            sendMobileAction('ch_up');
          });
        }

        if (elements.channelDownButton) {
          on(elements.channelDownButton, 'click', () => {
            flashRemoteIrDot();
            sendMobileAction('ch_down');
          });
        }

        if (elements.txtButton) {
          on(elements.txtButton, 'click', () => {
            flashRemoteIrDot();
            sendMobileAction('txt');
          });
        }

        if (elements.muteButton) {
          on(elements.muteButton, 'click', () => {
            flashRemoteIrDot();
            sendMobileAction('mute');
          });
        }

        if (elements.fullscreenButton) {
          on(elements.fullscreenButton, 'click', () => {
            flashRemoteIrDot();
            sendMobileAction('fullscreen');
          });
        }

        if (elements.contactButton) {
          on(elements.contactButton, 'click', () => {
            flashRemoteIrDot();
            sendMobileAction('contact');
          });
        }
      }

      function updateMobileDisplay() {
        const displayElement = elements.display;
        if (!displayElement) return;

        // When the TV is powered off, the mobile display should go blank.
        if (!state.isPowered) {
          displayElement.classList.add('power-off');
          displayElement.textContent = '';
          return;
        }
        displayElement.classList.remove('power-off');

        const titleFromIndex =
          mobileChannelTitles[mobileCurrentChannel] !== undefined
            ? mobileChannelTitles[mobileCurrentChannel]
            : '';
        const titleToShow = toUppercaseDisplayText(mobileCurrentTitle || titleFromIndex || '');

        const categoryFromIndex =
          mobileChannelCategoryLabels[mobileCurrentChannel] !== undefined
            ? mobileChannelCategoryLabels[mobileCurrentChannel]
            : '';
        const categoryToShow = toUppercaseDisplayText(mobileCurrentCategoryLabel || categoryFromIndex || '');

        const descriptionFromIndex =
          mobileChannelDescriptions[mobileCurrentChannel] !== undefined
            ? mobileChannelDescriptions[mobileCurrentChannel]
            : '';
        const descriptionToShow = toUppercaseDisplayText(
          (mobileCurrentDescription || descriptionFromIndex || '').trim()
        );

        const lines = [];
        if (categoryToShow) lines.push(categoryToShow);
        lines.push(`CH ${String(mobileCurrentChannel + 1).padStart(2, '0')}`);
        if (titleToShow) lines.push(titleToShow);
        if (descriptionToShow) lines.push(descriptionToShow);
        renderTicker(displayElement, lines.join('\n'));
      }

      bindMobileControlButtons();
      updateMobileStatus('Connecting...');
      updateMobileDisplay();

      try {
        const { default: Peer } = await import('peerjs');
        mobilePeer = new Peer();

        mobilePeer.on('open', () => {
          mobileConnection = mobilePeer.connect(hostId);

          mobileConnection.on('open', () => {
            updateMobileDisplay();
          });

          mobileConnection.on('data', data => {
            if (data.type === 'info') {
              mobileCurrentChannel = data.current;
              if (Array.isArray(data.channelTitles)) mobileChannelTitles = data.channelTitles;
              else if (Array.isArray(data.titles)) mobileChannelTitles = data.titles;
              if (Array.isArray(data.channelDescriptions)) {
                mobileChannelDescriptions = data.channelDescriptions;
              }
              if (Array.isArray(data.channelCategoryLabels)) {
                mobileChannelCategoryLabels = data.channelCategoryLabels;
              }
              mobileCurrentTitle = data.title !== undefined && data.title !== null ? String(data.title) : '';
              mobileCurrentDescription =
                data.description !== undefined && data.description !== null ? String(data.description).trim() : '';
              mobileCurrentCategoryLabel =
                data.categoryLabel !== undefined && data.categoryLabel !== null ? String(data.categoryLabel).trim() : '';

              ui.setPlayPaused(Boolean(data.paused));
              if (data.muted !== undefined) ui.setMuted(Boolean(data.muted));

              if (data.powered !== undefined) {
                state.isPowered = Boolean(data.powered);
                setRemoteIrDotPowerState();
              }

              updateMobileDisplay();
            }

            if (data.type === 'state') {
              mobileCurrentChannel = data.current;
              if (Array.isArray(data.channelTitles)) mobileChannelTitles = data.channelTitles;
              else if (Array.isArray(data.titles)) mobileChannelTitles = data.titles;
              if (Array.isArray(data.channelDescriptions)) {
                mobileChannelDescriptions = data.channelDescriptions;
              }
              if (Array.isArray(data.channelCategoryLabels)) {
                mobileChannelCategoryLabels = data.channelCategoryLabels;
              }
              mobileCurrentTitle = data.title !== undefined && data.title !== null ? String(data.title) : '';
              mobileCurrentDescription =
                data.description !== undefined && data.description !== null ? String(data.description).trim() : '';
              mobileCurrentCategoryLabel =
                data.categoryLabel !== undefined && data.categoryLabel !== null ? String(data.categoryLabel).trim() : '';

              ui.setPlayPaused(Boolean(data.paused));
              if (data.muted !== undefined) ui.setMuted(Boolean(data.muted));

              if (data.powered !== undefined) {
                state.isPowered = Boolean(data.powered);
                setRemoteIrDotPowerState();
              }

              updateMobileDisplay();
            }
          });

          mobileConnection.on('close', () => {
            updateMobileStatus('Disconnected');
          });

          mobileConnection.on('error', () => {
            updateMobileStatus('Connection failed');
          });
        });

        mobilePeer.on('error', () => {
          updateMobileStatus('Connection failed');
        });
      } catch (mobilePeerInitializationError) {
        console.warn('PeerJS unavailable:', mobilePeerInitializationError);
        updateMobileStatus('Connection failed');
      }

      syncAllFullscreenButtonIcons();
    }

    // ── Keyboard shortcuts ──
    const onKeyDown = keyboardEvent => {
      if (!state.isPowered) return;
      switch (keyboardEvent.key) {
        case 'ArrowUp':
          keyboardEvent.preventDefault();
          channelUp();
          break;
        case 'ArrowDown':
          keyboardEvent.preventDefault();
          channelDown();
          break;
        case ' ':
          keyboardEvent.preventDefault();
          togglePlayPause();
          break;
        case 'm':
        case 'M':
          toggleMute();
          break;
      }
    };
    on(document, 'keydown', onKeyDown);
    bindPauseOnHiddenTab();

    // ── Init ──
    if (!checkMobileRemote()) initializeChannelsFromOpenSheet();
  }
}

